import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Bell, Search, ShieldCheck, Server, Clock, ChevronRight,
  LayoutDashboard, BookOpen, Package, Warehouse, ArrowLeftRight,
  ClipboardCheck, ShieldAlert, Truck, QrCode, ShoppingCart, FileText,
  CheckCircle2, Zap, Settings as SettingsIcon, Globe, TrendingUp, TrendingDown,
  Database, Mail, KeyRound, ArrowLeft
} from "lucide-react";

/* ----------------------------- Top Data Ribbon ---------------------------- */
function TopRibbon() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hms = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return (
    <div className="h-9 bg-[#0B1120] text-slate-200 border-b border-slate-800 flex items-stretch text-[11px] font-mono select-none" data-testid="preview-top-ribbon">
      {/* LEFT */}
      <div className="flex items-center gap-4 px-4 border-r border-slate-800">
        <div className="flex items-center gap-1.5" data-testid="ribbon-server-status">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="text-emerald-400 font-semibold tracking-widest">PROD-MX-CHIHUAHUA</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-400">99.99% UP</span>
        </div>
        <div className="flex items-center gap-1.5 text-slate-300" data-testid="ribbon-live-clock">
          <Clock size={11} className="text-slate-500" />
          <span className="tabular-nums font-semibold">{hms}</span>
          <span className="text-slate-500">CST</span>
        </div>
        <div className="flex items-center gap-1 text-slate-400" data-testid="ribbon-breadcrumb">
          <span className="text-slate-500">MFIS</span>
          <ChevronRight size={10} className="text-slate-600" />
          <span className="text-slate-500">Compras</span>
          <ChevronRight size={10} className="text-slate-600" />
          <span className="text-white font-semibold">Panel de Control</span>
        </div>
      </div>

      {/* CENTER: Ticker KPIs */}
      <div className="flex-1 flex items-center justify-center gap-6 overflow-hidden">
        <TickerKpi label="REQ PEND" value="12" trail="$1.4M MXN" trend="+2 hoy" trendUp color="text-amber-400" testid="ribbon-kpi-req" />
        <span className="text-slate-700">|</span>
        <TickerKpi label="OC ABIERTAS" value="28" trail="$8.9M MXN" trend="Normal" color="text-blue-400" testid="ribbon-kpi-oc" />
        <span className="text-slate-700">|</span>
        <TickerKpi label="STOCK ALERTA" value="4" trail="SKUs" trend="Atención" trendUp color="text-rose-400" testid="ribbon-kpi-stock" />
      </div>

      {/* RIGHT */}
      <div className="flex items-center gap-3 px-4 border-l border-slate-800">
        <button className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors" data-testid="ribbon-search-btn">
          <Search size={11} />
          <span>Buscar</span>
          <kbd className="ml-1 px-1 py-0.5 bg-slate-800 border border-slate-700 rounded text-[9px]">⌘K</kbd>
        </button>
        <div className="flex items-center gap-1 text-emerald-400" data-testid="ribbon-smtp-badge">
          <Mail size={10} />
          <span className="text-[10px] font-semibold uppercase tracking-widest">SMTP</span>
        </div>
        <div className="flex items-center gap-1 text-blue-300" data-testid="ribbon-trust-badge" title="AES-256 · JWT · Backups cada 6h">
          <ShieldCheck size={10} />
          <span className="text-[10px] font-semibold uppercase tracking-widest">AES-256</span>
        </div>
        <button className="relative text-slate-400 hover:text-white" data-testid="ribbon-notifications">
          <Bell size={12} />
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-[#F29A2E] text-[8px] text-black font-bold rounded-full flex items-center justify-center">3</span>
        </button>
        <div className="flex items-center gap-2 pl-3 border-l border-slate-800" data-testid="ribbon-user-profile">
          <div className="w-5 h-5 bg-[#F29A2E] text-[#0B1120] font-black text-[10px] flex items-center justify-center rounded-full">OC</div>
          <div className="leading-tight text-[10px]">
            <div className="text-white font-semibold">Oswaldo Cereceres</div>
            <div className="text-slate-500 uppercase tracking-widest text-[9px]">Admin · Mina Pinos Altos</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TickerKpi({ label, value, trail, trend, trendUp, color, testid }) {
  const Icon = trendUp ? TrendingUp : TrendingDown;
  return (
    <div className="flex items-center gap-2 whitespace-nowrap" data-testid={testid}>
      <span className="text-slate-500 uppercase tracking-widest text-[10px]">{label}</span>
      <span className={`font-bold tabular-nums ${color}`}>{value}</span>
      <span className="text-slate-500 text-[10px]">{trail}</span>
      <span className={`flex items-center gap-0.5 text-[10px] ${color}`}>
        <Icon size={9} /> {trend}
      </span>
    </div>
  );
}

/* ------------------------------- Sidebar ---------------------------------- */
const NAV = [
  { grp: "Núcleo", items: [
    { icon: LayoutDashboard, label: "Panel de Control", active: true },
    { icon: BookOpen, label: "Libreta de Direcciones" },
    { icon: Package, label: "Maestro de Artículos" },
    { icon: FileText, label: "Cardex de Inventario" },
  ]},
  { grp: "Almacén (WMS)", items: [
    { icon: Warehouse, label: "Ubicaciones" },
    { icon: ArrowLeftRight, label: "Transferencias" },
    { icon: ClipboardCheck, label: "Conteos Cíclicos" },
    { icon: ShieldAlert, label: "Cuarentena / Calidad" },
    { icon: Truck, label: "Pick / Pack / Ship" },
    { icon: QrCode, label: "Escáner (Móvil)" },
  ]},
  { grp: "Compras", items: [
    { icon: ClipboardCheck, label: "Requisiciones", badge: "12" },
    { icon: ShoppingCart, label: "Órdenes de Compra", badge: "28" },
    { icon: FileText, label: "Recepciones" },
    { icon: CheckCircle2, label: "Conciliación 3 Vías" },
    { icon: Zap, label: "MRP / Reabastecimiento" },
  ]},
  { grp: "Sistema", items: [{ icon: SettingsIcon, label: "Ajustes" }] },
];

function Sidebar() {
  return (
    <aside className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col" data-testid="preview-sidebar">
      {/* Brand header with 3 lines */}
      <div className="p-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 bg-[#1F3E7C] text-white flex items-center justify-center rounded-sm shadow-sm">
          <span className="font-black text-sm">M</span>
        </div>
        <div className="min-w-0">
          <div className="font-black text-sm tracking-tight text-[#1F3E7C] leading-none">MFIS SUPPLY-CHAIN</div>
          <div className="text-[10px] font-bold text-[#F29A2E] uppercase tracking-widest mt-0.5">BY OOMINING</div>
          <div className="text-[9px] text-slate-500 uppercase tracking-[0.15em] mt-0.5">Mine Management Software</div>
        </div>
      </div>
      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV.map((g) => (
          <div key={g.grp} className="mb-4">
            <div className="px-4 py-1 text-[9px] font-bold text-slate-400 tracking-[0.2em] uppercase">{g.grp}</div>
            {g.items.map((it) => (
              <div key={it.label}
                className={`flex items-center gap-2.5 px-4 py-2 text-xs font-medium border-l-2 transition-colors ${
                  it.active
                    ? "border-[#F29A2E] bg-[#FEF3E2] text-[#1F3E7C] font-bold"
                    : "border-transparent text-slate-700 hover:bg-slate-50 hover:text-[#1F3E7C]"
                }`}>
                <it.icon size={14} className={it.active ? "text-[#F29A2E]" : "text-slate-400"} />
                <span className="flex-1">{it.label}</span>
                {it.badge && (
                  <span className="text-[10px] font-bold tabular-nums bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm">{it.badge}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </nav>
      {/* Footer with trust signals */}
      <div className="border-t border-slate-200 p-3 space-y-2">
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <ShieldCheck size={11} className="text-emerald-500" />
          <span className="uppercase tracking-widest font-semibold">Auditado · ISO-27001</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <Database size={11} className="text-blue-500" />
          <span>Respaldo · hace 34m</span>
        </div>
      </div>
    </aside>
  );
}

/* ------------------------------- Dashboard -------------------------------- */
const KPIS = [
  { label: "Requisiciones Pendientes", value: "12", trend: "+2 vs. ayer", trendUp: true, spark: "amber" },
  { label: "OC Abiertas", value: "28", trend: "$8.9M MXN", trendUp: null, spark: "blue" },
  { label: "Valor de Inventario", value: "$14.2M", trend: "+3.2% mes", trendUp: true, spark: "emerald" },
  { label: "Artículos Activos", value: "1,247", trend: "36 nuevos", trendUp: true, spark: "slate" },
  { label: "Facturas por Conciliar", value: "6", trend: "$420k MXN", trendUp: false, spark: "amber" },
  { label: "Stock bajo Mínimo", value: "4", trend: "Requiere OC", trendUp: null, spark: "rose", alert: true },
];

function Kpi({ label, value, trend, trendUp, spark, alert }) {
  const trendColor = trendUp === true ? "text-emerald-600" : trendUp === false ? "text-rose-600" : "text-slate-500";
  const sparkGradient = {
    amber: "from-amber-100 to-amber-50",
    blue: "from-blue-100 to-blue-50",
    emerald: "from-emerald-100 to-emerald-50",
    slate: "from-slate-100 to-slate-50",
    rose: "from-rose-100 to-rose-50",
  }[spark] || "from-slate-100 to-slate-50";
  return (
    <div className={`bg-white border ${alert ? "border-rose-300" : "border-slate-200"} rounded-sm p-4 relative overflow-hidden shadow-sm hover:shadow transition-shadow`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${sparkGradient} opacity-40 pointer-events-none`} />
      <div className="relative">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
        <div className={`text-3xl font-black tabular-nums ${alert ? "text-rose-600" : "text-[#1F3E7C]"}`}>{value}</div>
        <div className={`text-[11px] mt-1 flex items-center gap-1 ${trendColor}`}>
          {trendUp === true && <TrendingUp size={11} />}
          {trendUp === false && <TrendingDown size={11} />}
          {trend}
        </div>
      </div>
    </div>
  );
}

function SystemHealthCard({ icon: Icon, title, value, sub, status }) {
  const dotColor = { good: "bg-emerald-500", warn: "bg-amber-500", alert: "bg-rose-500", off: "bg-slate-400" }[status] || "bg-slate-400";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={13} className="text-[#1F3E7C]" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{title}</span>
        </div>
        <span className={`w-2 h-2 ${dotColor} rounded-full`}></span>
      </div>
      <div className="text-lg font-black tabular-nums text-[#1F3E7C]">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function Dashboard() {
  return (
    <main className="flex-1 overflow-y-auto bg-slate-50">
      {/* Page header */}
      <div className="px-8 pt-6 pb-4 border-b border-slate-200 bg-white">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#F29A2E] mb-1">Panel Ejecutivo</div>
            <h1 className="text-2xl font-black tracking-tight text-[#1F3E7C]">Panel de Control</h1>
            <p className="text-sm text-slate-500 mt-1">Estado en tiempo real de operaciones · Actualizado hace 12 s</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:border-[#1F3E7C] text-slate-700 text-xs font-bold uppercase tracking-widest transition-colors rounded-sm">
              <Globe size={12} /> ES
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1F3E7C] hover:bg-[#12264F] text-white text-xs font-bold uppercase tracking-widest transition-colors rounded-sm">
              <Activity size={12} /> Ver reporte
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {KPIS.map((k) => <Kpi key={k.label} {...k} />)}
        </div>

        {/* System Health */}
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Salud del Sistema</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <SystemHealthCard icon={Database} title="Último Respaldo" value="hace 34m" sub="47 KB · 20 colecciones" status="good" />
          <SystemHealthCard icon={Zap} title="Última Corrida MRP" value="hace 2h" sub="Manual · 0 OCs generadas" status="warn" />
          <SystemHealthCard icon={Mail} title="Correos MRP (Histórico)" value="19 corridas" sub="0 fallidos · 0 pendientes" status="good" />
          <SystemHealthCard icon={KeyRound} title="Servicio Resend" value="Activo" sub="onboarding@resend.dev" status="good" />
        </div>

        {/* Two-column: table + activity */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Alertas de Stock Mínimo</div>
                <div className="text-sm font-bold text-[#1F3E7C]">4 SKUs requieren atención</div>
              </div>
              <button className="text-[11px] font-bold uppercase tracking-widest text-[#F29A2E] hover:text-[#D8831B]">
                Generar Requisición →
              </button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Artículo</th>
                  <th className="text-left px-4 py-2">Descripción</th>
                  <th className="text-right px-4 py-2">Mínimo</th>
                  <th className="text-right px-4 py-2">Disponible</th>
                  <th className="text-right px-4 py-2">Faltante</th>
                  <th className="text-right px-4 py-2">Costo Est.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ["ITM-201", "Motorreductor 1:30 2HP", 1, 0, "1 EA", "$12,300.00"],
                  ["ITM-601", "Cadena de rodillos ANSI 60 (caja 3m)", 4, 0, "4 CAJA", "$3,560.00"],
                  ["ITM-108", "Filtro hidráulico HP-27", 6, 2, "4 EA", "$960.00"],
                  ["ITM-322", "Válvula esfera 2\" NPT", 8, 3, "5 EA", "$2,240.00"],
                ].map(([sku, desc, m, d, f, c], i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-bold text-[#1F3E7C] tabular-nums">{sku}</td>
                    <td className="px-4 py-2.5 text-slate-700">{desc}</td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-slate-500">{m}</td>
                    <td className="text-right px-4 py-2.5 tabular-nums text-rose-600 font-bold">{d}</td>
                    <td className="text-right px-4 py-2.5 tabular-nums font-bold text-slate-800">{f}</td>
                    <td className="text-right px-4 py-2.5 tabular-nums font-bold text-[#1F3E7C]">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Activity feed */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Actividad Reciente</div>
              <div className="text-sm font-bold text-[#1F3E7C]">Últimos movimientos</div>
            </div>
            <ul className="divide-y divide-slate-100">
              {[
                { t: "hace 3m", user: "Oswaldo C.", act: "aprobó REQ-00047", amt: "$45,200" },
                { t: "hace 12m", user: "Aprobador A.", act: "rechazó REQ-00046", amt: "" },
                { t: "hace 34m", user: "Sistema", act: "respaldo automático", amt: "47 KB" },
                { t: "hace 1h", user: "Almacén 3", act: "recibió RCP-00021", amt: "$18,900" },
                { t: "hace 2h", user: "MRP Auto", act: "corrió sin sugerencias", amt: "" },
              ].map((e, i) => (
                <li key={i} className="px-4 py-2.5 text-[11px]">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <span className="font-bold text-slate-800">{e.user}</span>{" "}
                      <span className="text-slate-500">{e.act}</span>
                    </div>
                    {e.amt && <span className="text-[10px] tabular-nums font-bold text-[#1F3E7C]">{e.amt}</span>}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{e.t}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Trust footer */}
        <div className="mt-8 pt-4 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-500 uppercase tracking-widest">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-500" /> Cifrado AES-256</span>
            <span className="flex items-center gap-1"><Server size={11} className="text-blue-500" /> ISO 27001 Compliant</span>
            <span className="flex items-center gap-1"><Database size={11} className="text-blue-500" /> Respaldos cada 6h</span>
          </div>
          <div>© 2026 OOMINING · Chihuahua, México</div>
        </div>
      </div>
    </main>
  );
}

/* ------------------------------ Main Preview ------------------------------ */
export default function DesignPreview() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="design-preview">
      {/* Preview banner */}
      <div className="bg-[#F29A2E] text-[#0B1120] text-[10px] font-bold uppercase tracking-[0.2em] py-1 px-4 text-center flex items-center justify-between">
        <Link to="/" className="flex items-center gap-1 hover:underline" data-testid="preview-back-link">
          <ArrowLeft size={11} /> Volver al sistema actual
        </Link>
        <span>Vista Previa · Diseño Propuesto · MFIS SUPPLY-CHAIN BY OOMINING · v2.0</span>
        <span className="text-[10px] font-mono">MOCKUP — no funcional</span>
      </div>

      <TopRibbon />
      <div className="flex" style={{ height: "calc(100vh - 36px - 24px)" }}>
        <Sidebar />
        <Dashboard />
      </div>
    </div>
  );
}
